import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { UploadProgressDialogComponent } from '../components/dialogs/upload-progress-dialog/upload-progress-dialog.component';
import { App, KHFileInfo, Project, ProjectNote, ProjectNotesServiceModel } from '../models';
import { DESIGN_CONSTANTS, DIALOG_TEXT_CONSTANTS, SNACKBAR_CONSTANTS } from '../shared/constants';
import { ProjectNotesDataService } from './data-services';

@Injectable({
    providedIn: 'root'
})
export class ProjectNotesService {

    private _projectNoteReady: Subject<boolean> = new Subject<boolean>();
    private _projectNotesReady: Subject<boolean> = new Subject<boolean>();
    private _projectNotesRequest: Subscription | undefined;
    private _projectNotesRequestProjectId: string | undefined;

    projectNoteReady$ = this._projectNoteReady.asObservable();
    projectNotesReady$ = this._projectNotesReady.asObservable();

    model: ProjectNotesServiceModel = new ProjectNotesServiceModel();

    constructor(
        private _snackBar: MatSnackBar,
        private _projectNotesDataService: ProjectNotesDataService,
        private _matDialog: MatDialog,
    ) { }

    createProjectNote(app: App, project: Project, projectNoteName: string): void {
        const projectNote = new ProjectNote();
        projectNote.name = projectNoteName;

        this._projectNotesDataService.createProjectNote(app, project, projectNote)
            .subscribe({
                next: ((projectNote: ProjectNote) => {
                    this.model.projectNotes.push(projectNote);
                    this.setProjectNote(projectNote);
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${app.aliases.project.singular.toLocaleLowerCase()} note`, 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }),
                complete: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${app.aliases.project.singular} note`, 'successfully created'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                    this.getProjectNotesByProjectId(app, project);
                }),
            });
    }

    updateProjectNote(app: App, projectNote: ProjectNote, updatedProjectNote: ProjectNote): void {
        this._projectNotesDataService.updateProjectNote(app, projectNote, updatedProjectNote)
            .subscribe({
                next: ((projectNote: ProjectNote) => {
                    this.model.projectNotes = this.model.projectNotes.map((note: ProjectNote) => {
                        if (note.id === projectNote.id) {
                            return projectNote;
                        }
                        return note;
                    });
                    this.setProjectNote(projectNote);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${app.aliases.project.singular} note`, 'successfully updated'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(app.aliases.project.singular.toLocaleLowerCase(), 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }),
            });
    }

    deleteProjectNote(app: App, projectNote: ProjectNote, project: Project): void {
        this._projectNotesDataService.deleteProjectNote(app, projectNote)
            .subscribe({
                next: (() => {
                    const deletedIndex = this.model.projectNotes.findIndex((note: ProjectNote) => note.id === projectNote.id);
                    this.model.projectNotes = this.model.projectNotes.filter((note: ProjectNote) => note.id !== projectNote.id);
                    const newIndex = Math.max(0, deletedIndex - 1);
                    this.setProjectNote(this.model.projectNotes[newIndex] ?? null);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(`${app.aliases.project.singular} note`, 'successfully deleted'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                    this.getProjectNotesByProjectId(app, project);
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${app.aliases.project.singular.toLocaleLowerCase()} note`, 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                })
            });
    }

    setProjectNote(projectNote: ProjectNote): void {
        this.model.currentProjectNote = projectNote;
        this._projectNoteReady.next(true);
    }

    getProjectNotesByProjectId(app: App, project: Project): void {
        if (project?.id === undefined) {
            return;
        }

        if (!app.showProjectNotes) {
            return;
        }

        if (this.model.loading && this._projectNotesRequestProjectId === project.id) {
            return;
        }

        const previousRequest = this._projectNotesRequest;
        this.model.loading = true;
        this._projectNotesRequestProjectId = project.id;

        const projectNotesRequest$ = project.isSummaryProject === true
            ? this._projectNotesDataService.getSummaryProjectProjectNotes(app, project)
            : this._projectNotesDataService.getProjectNotesByProjectId(app, project);

        const projectNotesRequest = projectNotesRequest$
            .subscribe({
                next: ((projectNotes: ProjectNote[]) => {
                    this.model.projectNotes = projectNotes;
                    this._projectNotesReady.next(true);
                    this._projectNoteReady.next(true);
                }),
                error: (() => {
                    this.model.projectNotes = new Array<ProjectNote>();
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(`${app.aliases.project.singular.toLowerCase()} note`, 'getting', true), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }),
            });

        this._projectNotesRequest = projectNotesRequest;
        projectNotesRequest.add(() => {
            if (this._projectNotesRequest === projectNotesRequest) {
                this.model.loading = false;
                this._projectNotesRequestProjectId = undefined;
            }
        });

        previousRequest?.unsubscribe();
    }

    // A summary project's loaded notes include its member projects' notes; this returns only the project's own.
    getProjectNotesForProject(projectId: string): ProjectNote[] {
        return (this.model.projectNotes ?? [])
            .filter((note: ProjectNote) => note.projectId === projectId);
    }

    uploadFile(app: App, projectNote: ProjectNote, file: FormData): void {
        const progressDialogRef = this._matDialog.open(UploadProgressDialogComponent, {
            width: DESIGN_CONSTANTS.DIALOG_CONFIRMATION_WIDTH,
            data: DIALOG_TEXT_CONSTANTS.UPLOAD_PROGRESS_TITLE(file.getAll('files').length),
            disableClose: true,
            autoFocus: false,
        });

        const cancelUpload$ = new Subject<void>();
        progressDialogRef.afterClosed().subscribe((wasCancelled: boolean) => {
            if (wasCancelled) {
                cancelUpload$.next();
            }
        });

        this._projectNotesDataService.uploadFile(app, projectNote, file)
            .pipe(
                takeUntil(cancelUpload$),
                finalize(() => {
                    progressDialogRef.close(false);
                })
            )
            .subscribe({
                next: ((projectNote: ProjectNote) => {
                    this.model.projectNotes = this.model.projectNotes.map((note: ProjectNote) => note.id === projectNote.id ? projectNote : note);
                    this.setProjectNote(projectNote);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('File', 'successfully uploaded'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('the file', 'uploading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }),
            });
    }

    deleteFile(app: App, projectNote: ProjectNote, file: KHFileInfo): void {
        this._projectNotesDataService.deleteFile(app, projectNote, file)
            .subscribe({
                next: ((projectNote: ProjectNote) => {
                    this.model.projectNotes = this.model.projectNotes.map((note: ProjectNote) => note.id === projectNote.id ? projectNote : note);
                    this.setProjectNote(projectNote);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('File', 'successfully deleted'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('the file', 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }),
            });
    }

    clearCurrentNote(): void {
        this.model.currentProjectNote = null;
    }
}
